import { addDays, addMonths, areIntervalsOverlapping, eachDayOfInterval, endOfMonth, format, getDay, isAfter, isWeekend, startOfMonth } from "date-fns"
import { memo} from "react";
import { isEqual, last } from 'lodash';
import { ClinicBlockAvailabilityForDates } from "../common/ParseEventsToAvailability";
import { DATE_FNS_DATE_ONLY_FORMAT } from "../common/constants";

type MonthProps = {
  monthStartDate: Date,
  availabilityInfoForEachDay: ClinicBlockAvailabilityForDates,
  selectDate?: (date: Date) => void,
  isDoingFirstLoad: boolean,
  className?: string,
}

function Month(props: MonthProps) {
  function getDaySegmentBackgroundClasses(date: Date, timeOfDay: 'morning' | 'afternoon'): string {
    let forground = 'tw-bg-red-300 dark:tw-bg-red-600';
    let background = "tw-bg-stripes-red-400"

    const clinicBlocksForDay = props.availabilityInfoForEachDay.get(format(date, DATE_FNS_DATE_ONLY_FORMAT));

    if (!clinicBlocksForDay) {
      // Covers events past the outlook sync horizon 
      if (isAfter(date, new Date(last(Array.from(props.availabilityInfoForEachDay.keys()).sort())!))) {
        return "tw-bg-slate-400"
      }

      return "tw-bg-red-300 dark:tw-bg-red-600 tw-bg-stripes-red-400"
    }

    const clinicBlockAvailability = clinicBlocksForDay.find(clinicBlock => {
      const DATE = new Date(date);

      if (timeOfDay === 'morning') {
        const TIME_nine = new Date(DATE.getTime());
        TIME_nine.setHours(9, 0, 0, 0)
        const TIME_noon = new Date(DATE.getTime());
        TIME_noon.setHours(12, 0, 0, 0)
        return areIntervalsOverlapping(
          { start: new Date(clinicBlock.startDateUTCMilliseconds), end: new Date(clinicBlock.endDateUTCMilliseconds) },
          { start: TIME_nine, end: TIME_noon })
      }

      const TIME_onethirty = new Date(DATE.getTime());
      TIME_onethirty.setHours(13, 30, 0, 0)
      const TIME_five = new Date(DATE.getTime());
      TIME_five.setHours(17, 0, 0, 0)
      return areIntervalsOverlapping(
        { start: new Date(clinicBlock.startDateUTCMilliseconds), end: new Date(clinicBlock.endDateUTCMilliseconds) },
        { start: TIME_onethirty, end: TIME_five })
    })

    if (clinicBlockAvailability) {
      const color = clinicBlockAvailability.color;

      switch(color) {
        case 'red':
          forground = "tw-bg-red-300 dark:tw-bg-red-600"
          background = clinicBlockAvailability.isHygieneWeek ? ' tw-bg-stripes-blue-400'  :' tw-bg-stripes-red-400' //No potentially schedulable event 
          break;
        case 'green':
          forground = clinicBlockAvailability.isHygieneWeek ? 'tw-bg-blue-400 dark:tw-bg-blue-500' : 'tw-bg-green-500 dark:tw-bg-green-600';
          background = ""
          break;
        case 'yellow':
          forground = 'tw-bg-yellow-400 dark:tw-bg-yellow-500'
          background = "" //Warning, potentially schedulable event time is short
          break;
        case 'yellow-striped':
          forground = 'tw-bg-yellow-400 dark:tw-bg-yellow-500'
          background = 'tw-bg-stripes-yellow-600' //Warning, potentially schedulable event time is short
          break;
      }
    }

    return forground + " " + background;
  }

  return (
    <div className={`tw-bg-white dark:tw-bg-slate-600 tw-rounded-lg tw-overflow-hidden tw-shadow dark:tw-text-slate-100 ${props.className} tw-border tw-border-slate-100`}>
      <div className="-tw-mx-1 -tw-mb-1">
        <div className="tw-py-2 tw-px-6">
          <div>
            <span className="tw-text-lg tw-font-bold tw-text-gray-800 dark:tw-text-gray-200">{format(props.monthStartDate, 'MMMM')}</span>
            <span className="tw-ml-1 tw-text-lg tw-text-gray-600 dark:tw-text-gray-300 tw-font-normal">{format(props.monthStartDate, 'yyyy')}</span>
          </div>
        </div>
        <div className="tw-grid tw-grid-cols-7 tw-auto-rows-fr tw-border-l tw-border-t dark:tw-border-slate-900">
          {
            ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map(dayOfWeek => (
              <div className="tw-py-2 tw-text-sm tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full tw-border-r dark:tw-border-slate-900 tw-text-gray-600 dark:tw-text-gray-200" key={dayOfWeek}>
                <span>{dayOfWeek.charAt(0)}</span>
                <span className="tw-hidden lg:tw-block">{dayOfWeek.substring(1)}</span>
              </div>
            ))
          }
        </div>
        <div className={`tw-grid tw-grid-cols-7 tw-auto-rows-fr tw-border-l tw-border-t dark:tw-border-slate-900 tw-relative tw-transition-all tw-ease-in-out tw-duration-300 ${props.isDoingFirstLoad ? 'motion-safe:tw-animate-pulse motion-reduce:tw-opacity-50' : ''}`}>
          {
            getDay(props.monthStartDate) > 0 ? <div className={`tw-col-span-${getDay(props.monthStartDate)}`}></div> : undefined
          }

          {
            eachDayOfInterval({
              start: startOfMonth(props.monthStartDate),
              end: endOfMonth(props.monthStartDate),
            }).map(date => {
              return <Day
                key={date.toString()}
                isDoingFirstLoad={props.isDoingFirstLoad}
                date={date}
                selectDate={props.selectDate}
                getDaySegmentBackgroundClasses={getDaySegmentBackgroundClasses}
              />
            })
          }
        </div>
      </div>
    </div>
  )
}

export default memo(Month, 
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  }
)

type DayProps = {
  date: Date,
  isDoingFirstLoad: boolean,
  getDaySegmentBackgroundClasses: (date: Date, timeOfDay: 'morning' | 'afternoon') => string, 
  selectDate?: (date: Date) => void,
}

function Day(props: DayProps) {
  const colorForWholeDay = (function(){
    if (props.isDoingFirstLoad) {
      return 'tw-bg-slate-400'
    }
  
    if (isAfter(new Date(), props.date)) {
      return 'tw-bg-gray-300 dark:tw-bg-gray-500'
    }
  
    if (isWeekend(props.date)) {
      return 'tw-bg-white dark:tw-bg-slate-900'
    }

    return undefined;
  })();

  const dayIsSelectable = !colorForWholeDay && (!props.getDaySegmentBackgroundClasses(props.date, 'morning').includes("red") || !props.getDaySegmentBackgroundClasses(props.date, 'afternoon').includes("red"));

  return (
    <div className={`tw-flex tw-justify-center tw-relative tw-w-full ${dayIsSelectable ? 'tw-cursor-pointer hover:tw-opacity-95' : '' }`} onClick={() => props.selectDate?.(props.date) }>
      <div className="tw-z-0 tw-w-full">
        <div className={`tw-w-full tw-h-5 md:tw-h-8 ${colorForWholeDay || props.getDaySegmentBackgroundClasses(props.date, 'morning')} tw-bg-stripes`}>
        </div>
        <div className={`tw-w-full tw-h-5 md:tw-h-8 ${colorForWholeDay || props.getDaySegmentBackgroundClasses(props.date, 'afternoon')} tw-bg-stripes tw-bg-[left_-5px] md:tw-bg-[left_-2px] sm:tw-bg-[left_-5px] `}>
        </div>
      </div>
      <div className="inset-0 tw-text-base tw-flex tw-items-center tw-justify-center tw-z-10 tw-absolute tw-w-full tw-h-full tw-border-r tw-border-b dark:tw-border-slate-900">
        {format(props.date, 'd')}
      </div>
    </div>
  )
}

export function generateEvents(): ClinicBlockAvailabilityForDates {
  let startingDate = startOfMonth(addMonths(new Date(),1));

  let fakeData: ClinicBlockAvailabilityForDates = new Map();
  for (let i=0; i<=62;i++){
    const day = addDays(startingDate,i);
    const TIME_nine = new Date(day.getTime());
    TIME_nine.setHours(9, 0, 0, 0)
    const TIME_noon = new Date(day.getTime());
    TIME_noon.setHours(12, 0, 0, 0)
    const TIME_onethirty = new Date(day.getTime());
    TIME_onethirty.setHours(13, 30, 0, 0)
    const TIME_five = new Date(day.getTime());
    TIME_five.setHours(17, 0, 0, 0)

    fakeData.set(format(day, DATE_FNS_DATE_ONLY_FORMAT), [
      {
        startDateUTCMilliseconds: TIME_nine.getTime(),
        endDateUTCMilliseconds: TIME_noon.getTime(),
        color: Math.random() > 0.75 ? (Math.random() > 0.25 ? "green": "yellow") : "red",
        isHygieneWeek: false,
        availableSlots: []
      },
      {
        startDateUTCMilliseconds: TIME_onethirty.getTime(),
        endDateUTCMilliseconds: TIME_five.getTime(),
        color: Math.random() > 0.75 ? (Math.random() > 0.25 ? "green": "yellow") : "red",
        isHygieneWeek: false,
        availableSlots: []
      }
    ]);
  }  
  
  return fakeData;
}